/** Check whether a WMS layer key is visible in the layer tree. */
export function isLayerKeyVisible(groups, key) {
  for (const group of Object.values(groups)) {
    if (!group.visible) continue;
    for (const [lKey, layer] of Object.entries(group.children)) {
      if (layer.children) {
        for (const [nKey, nLayer] of Object.entries(layer.children)) {
          if (nKey === key) return nLayer.visible;
        }
      } else if (lKey === key) {
        return layer.visible;
      }
    }
  }
  return false;
}

/** Build set of layer keys eligible for identify/popup queries. */
export function buildPopupLayerKeys(groups, excluded = []) {
  const keys = new Set();
  for (const group of Object.values(groups)) {
    if (!group.visible) continue;
    for (const [lKey, layer] of Object.entries(group.children)) {
      if (layer.children) {
        for (const [nKey, nLayer] of Object.entries(layer.children)) {
          if (nLayer.visible && !excluded.includes(nKey)) keys.add(nKey);
        }
      } else if (layer.visible && !excluded.includes(lKey)) {
        keys.add(lKey);
      }
    }
  }
  return keys;
}

/** Immutable toggle for a single layer checkbox. */
export function toggleLayerInGroups(prev, groupKey, layerKey, nestedKey = null) {
  const group = prev[groupKey];
  if (!group) return prev;

  if (nestedKey) {
    const parent = group.children[layerKey];
    const nested = parent?.children?.[nestedKey];
    if (!nested) return prev;
    return {
      ...prev,
      [groupKey]: {
        ...group,
        children: {
          ...group.children,
          [layerKey]: {
            ...parent,
            children: {
              ...parent.children,
              [nestedKey]: { ...nested, visible: !nested.visible },
            },
          },
        },
      },
    };
  }

  const layer = group.children[layerKey];
  if (!layer) return prev;
  return {
    ...prev,
    [groupKey]: {
      ...group,
      children: {
        ...group.children,
        [layerKey]: { ...layer, visible: !layer.visible },
      },
    },
  };
}

/** Immutable toggle for entire group visibility. */
export function toggleGroupInGroups(prev, groupKey) {
  const group = prev[groupKey];
  if (!group) return prev;
  const newVisibility = !group.visible;
  const newChildren = {};

  for (const [lKey, layer] of Object.entries(group.children)) {
    if (layer.children) {
      const newNested = {};
      for (const [nKey, nLayer] of Object.entries(layer.children)) {
        newNested[nKey] = { ...nLayer, visible: newVisibility };
      }
      newChildren[lKey] = { ...layer, children: newNested };
    } else {
      newChildren[lKey] = { ...layer, visible: newVisibility };
    }
  }

  return {
    ...prev,
    [groupKey]: { ...group, visible: newVisibility, children: newChildren },
  };
}

/** Immutable toggle for nested group expand/collapse. */
export function toggleNestedExpandedInGroups(prev, groupKey, layerKey) {
  const group = prev[groupKey];
  const parent = group?.children?.[layerKey];
  if (!parent) return prev;
  return {
    ...prev,
    [groupKey]: {
      ...group,
      children: {
        ...group.children,
        [layerKey]: { ...parent, expanded: !parent.expanded },
      },
    },
  };
}

/** Sync OpenLayers tile layer visibility after React state update. */
export function syncTileLayerForKey(
  groups,
  targetKey,
  groupKey,
  wmsLayersRef,
  layerExtentsRef,
) {
  const olLayer = wmsLayersRef.current[targetKey];
  if (!olLayer) return;

  const groupVisible = groups[groupKey]?.visible;
  const sharedKeys = Object.keys(wmsLayersRef.current).filter(
    (k) => wmsLayersRef.current[k] === olLayer,
  );

  const visibleLayerNames = sharedKeys
    .filter((k) => isLayerKeyVisible(groups, k))
    .map((k) => layerExtentsRef.current[k])
    .filter(Boolean);

  const shouldShow = groupVisible && visibleLayerNames.length > 0;
  olLayer.setVisible(shouldShow);
  if (shouldShow) {
    olLayer.getSource().updateParams({
      LAYERS: visibleLayerNames.join(","),
    });
  }
}

/** Sync all tile layers in a group after group-level toggle. */
export function syncGroupTileLayers(groups, groupKey, wmsLayersRef, layerExtentsRef) {
  const group = groups[groupKey];
  if (!group) return;

  const uniqueLayers = new Set();
  const collectKeys = (layerKey, nestedKey) => {
    const key = nestedKey || layerKey;
    const olLayer = wmsLayersRef.current[key];
    if (olLayer) uniqueLayers.add({ olLayer, key });
  };

  Object.entries(group.children).forEach(([layerKey, layer]) => {
    if (layer.children) {
      Object.keys(layer.children).forEach((nestedKey) =>
        collectKeys(layerKey, nestedKey),
      );
    } else {
      collectKeys(layerKey, null);
    }
  });

  uniqueLayers.forEach(({ olLayer, key }) => {
    const sharedKeys = Object.keys(wmsLayersRef.current).filter(
      (k) => wmsLayersRef.current[k] === olLayer,
    );
    const visibleLayerNames = sharedKeys
      .filter((k) => isLayerKeyVisible(groups, k))
      .map((k) => layerExtentsRef.current[k])
      .filter(Boolean);

    const shouldShow = group.visible && visibleLayerNames.length > 0;
    olLayer.setVisible(shouldShow);
    if (shouldShow) {
      olLayer.getSource().updateParams({
        LAYERS: visibleLayerNames.join(","),
      });
    }
  });
}
